import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { Loader2 } from 'lucide-react';

interface GlobalAdminProtectedRouteProps {
  children: React.ReactNode;
}

export const GlobalAdminProtectedRoute: React.FC<GlobalAdminProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isGlobalAdmin, isSupportAgent, isLoading: adminLoading } = useGlobalAdminStatus();

  const isLoading = authLoading || adminLoading;

  const getLoadingStatus = () => {
    if (authLoading) return "Authenticating...";
    if (adminLoading) return "Checking permissions...";
    return "Loading...";
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
        <div className="mb-8 flex items-center gap-3">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-foreground"
          >
            <path
              d="M12 2L2 7L12 12L22 7L12 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 17L12 22L22 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 12L12 17L22 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-2xl font-bold text-foreground">BitSwan</span>
        </div>

        <div className="mb-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>

        <p className="text-sm text-muted-foreground animate-pulse">
          {getLoadingStatus()}
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isGlobalAdmin && !isSupportAgent) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You do not have permission to access the Global Admin portal.
            Please contact your administrator if you believe this is an error.
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Return to Login
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
